import React from 'react';
import { Mic, Square, X, Loader2 } from 'lucide-react';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import type { TranscriptionResponse } from '../../services/voice-api';
import WaveformVisualizer from './WaveformVisualizer';
import toast from 'react-hot-toast';

interface VoiceInputButtonProps {
  onTranscriptReady: (result: TranscriptionResponse) => void;
  disabled?: boolean;
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({ onTranscriptReady, disabled }) => {
  const {
    state,
    startRecording,
    stopRecording,
    cancelRecording,
    analyserNode,
    duration,
  } = useVoiceRecorder({
    onTranscriptReady,
    onError: (msg) => toast.error(msg),
  });

  if (state === 'recording' && analyserNode) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 rounded-full bg-destructive/15 px-3 py-1.5">
          <span className="h-2 w-2 rounded-full bg-destructive animate-pulse" />
          {analyserNode && (
            <WaveformVisualizer analyserNode={analyserNode} className="h-6 w-24" />
          )}
          <span className="text-xs font-medium text-destructive tabular-nums min-w-[5ch]">
            {formatDuration(duration)} / 2:00
          </span>
        </div>
        <button
          onClick={stopRecording}
          className="flex items-center justify-center w-8 h-8 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
          title="Stop & transcribe"
        >
          <Square className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={cancelRecording}
          className="flex items-center justify-center w-8 h-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Cancel recording"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (state === 'transcribing') {
    return (
      <div className="flex items-center gap-2 rounded-full bg-accent px-3 py-1.5">
        <Loader2 className="w-4 h-4 animate-spin text-primary" />
        <span className="text-xs font-medium text-primary">
          Transcribing...
        </span>
      </div>
    );
  }

  const isDisabled = disabled || state === 'requesting';

  return (
    <button
      onClick={startRecording}
      disabled={isDisabled}
      className="flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-primary hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      title="Voice input"
    >
      {state === 'requesting' ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </button>
  );
};

export default VoiceInputButton;
